import { defineComponent as _defineComponent } from 'vue'
import BButton from 'JS/components/BButton.vue';
import LazyImage from 'JS/components/LazyImage.vue';
import Price from './price/ProductListPrice.vue';
import ProductFlag from './ProductFlag.vue';
import ProductService, { AddToCartResult, IPDPAddToCartResponse } from 'JS/services/ProductService';
import ProductStockInfo from './ProductStockInfo.vue';
import ProductSwatches from './swatches/ProductSwatches.vue';
import PromoLabel from './promos/PromoLabel.vue';
import ReviewStars from 'JS/components/ReviewStars.vue';
import { CatalogState } from 'JS/catalog/store/store';
import { computed, inject, onBeforeMount, ref } from 'vue';
import { EventBusEvents } from 'JS/types/EventBus';
import { FulfillmentType } from 'JS/types/ProductDelivery';
import { getPriceMode, IListableProductJson, IProductSwatch, ProductListProductClickRequest } from 'JS/types/Product';
import { useLegacyVuexStore } from 'JS/composables/legacy-vuex-store';


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductSummaryCard',
  props: {
    product: null,
    index: null,
    hawkIndex: null,
    itemListId: null,
    itemListName: null
  },
  emits: ["product-link-click"],
  setup(__props: any, { emit }: { emit: ({
	(event: 'product-link-click', productTrackingRequest: ProductListProductClickRequest): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	product: IListableProductJson,
	index: number, // used for click tracking
	hawkIndex?: number, // used for tracking in hawk search
	itemListId: string,
	itemListName: string
}

const store = useLegacyVuexStore<CatalogState>();
const productService = inject<ProductService>('productService');





const bopusOnly = computed(() => store.state.bopusOnly);
const loading = computed({
	get: () => {
		return store.state.isLoadingAddToCart;
	},
	set: (val) => {
		store.commit('isLoadingAddToCart', val);
	}
});
const wrapperClasses = computed(() => {
	return [
		'psc-wrapper',
		{
			'no-flag': !hasFlag.value
		}
	];
});
const productImage = computed(() => activeProductImage.value ? activeProductImage.value : props.product.imageUrl);
const productImageWebp = computed(() => activeProductImage.value ? activeProductImageWebp.value : props.product.imageUrlWebp);
const flagText = computed(() => {
	if (props.product.isNew) {
		return 'New';
	} else if (props.product.isHighestRated) {
		return 'Best Rated';
	} else if (props.product.isBestSeller) {
		return 'Best Seller';
	}
	return null;
});
const hasFlag = computed(() => props.product.isNew || props.product.isHighestRated || props.product.isBestSeller);
const activeProductImage = computed(() => activeSwatch.value?.largeImage);
const activeProductImageWebp = computed(() => activeSwatch.value?.largeImageWebp);
const activeSwatchColor = computed(() => activeSwatch.value ? activeSwatch.value.color : '');
const productImageAltText = computed(() => {
	const color = activeSwatch.value?.color;

	let prefix = '';
	if (color) {
		prefix = `Showing ${color} variant of `;
	} else {
		prefix = 'Photo of ';
	}

	return prefix + props.product.productName;
});
const isProductInCart = computed(() => {
	return store.state.productsInCart?.has(props.product.blainNumber) ||
		(props.product.groupID && store.state.productsInCart?.has(props.product.groupID.toString()));
});
const canAddToCart = computed(() => !props.product.groupID &&
	!props.product.isRestrictedProduct &&
	!props.product.isSoldOut &&
	!props.product.isComingSoon && (
	(props.product.activeOnECommerce && props.product.shipEcomOnHand > 0) ||
	(props.product.activeInStorePickup && props.product.storeEcomOnHand > 0) ||
	(props.product.activeLocalDelivery && props.product.storeEcomOnHand > 0))
);
const atcAriaLabel = computed(() => {
	let lbl = '';
	if (!canAddToCart.value) {
		lbl = `View details of ${props.product.productName}`;
	} else if (productAdded.value) {
		lbl = `Added ${props.product.productName} to cart`;
	} else if (loading.value && loading.value === props.product.blainNumber) {
		lbl = `Adding ${props.product.productName} to cart`;
	} else if (productAddError.value) {
		lbl = `Could not add ${props.product.productName} to cart. Try again`;
	} else {
		lbl = `Add ${props.product.productName} to cart`;
	}

	return lbl;
});

const activeSwatch = ref<IProductSwatch>();
const shouldImageTransition = ref(true);
const productAdded = ref(false);
const productAddError = ref(false);

function onSwatchChange(swatch: IProductSwatch) {
	activeSwatch.value = swatch;
	shouldImageTransition.value = false;
}

function onProductLinkClick(event) {
	const productTrackingRequest: ProductListProductClickRequest = {
		event,
		blainNumber: props.product.blainNumber,
		index: props.index,
		hawkIndex: props.hawkIndex,
		vendorBrand: props.product.vendorBrand,
		productName: props.product.productName,
		currentPrice: props.product.sellingPrice,
		mapSafePrice: props.product.mapSafePrice, 
		belowMap: props.product.price.isBelowMAP,
		priceMode: getPriceMode(props.product.price),
		itemListID: props.itemListId,
		itemListName: props.itemListName,
		sponsoredProductInfo: props.product.sponsoredProductInfo
	};
	emit('product-link-click', productTrackingRequest);
}

function getExpectedFulfillmentMethod(): FulfillmentType {
	const canShip = props.product.activeOnECommerce && props.product.shipEcomOnHand > 0;
	const canPickup = props.product.activeInStorePickup && props.product.storeEcomOnHand > 0;
	const canDeliver = props.product.activeLocalDelivery && props.product.storeEcomOnHand > 0;

	const hasItemsToShip = store.state.cartHasItemsToShip;
	const hasPickupItems = store.state.cartHasPickupItems;
	const hasDeliveryItems = store.state.cartHasDeliveryItems;

	// if no existing cart
	if (!store.state.productsInCart || store.state.productsInCart.size === 0) {
		// return bopus if bopusOnly selected or not available for ship, else return ship
		return (bopusOnly.value || !canShip) ? FulfillmentType.InStorePickup : FulfillmentType.StandardShipping;
	}

	// if bopusOnly is selected
	if (bopusOnly.value) {
		// if cart has only LD items and item is available for LD, return LD
		if (hasDeliveryItems && canDeliver && !hasPickupItems) {
			return FulfillmentType.LocalDelivery;
		}
		// else return bopus
		return FulfillmentType.InStorePickup;
	}

	// if cart has items for ship
	if (hasItemsToShip) {
		// if cart also has bopus items and item is available for bopus, return bopus
		if (hasPickupItems && canPickup) {
			return FulfillmentType.InStorePickup;
		}
		// if cart also has LD items and item is available for LD and not available for ship, return LD
		if (hasDeliveryItems && canDeliver && !canShip) {
			return FulfillmentType.LocalDelivery;
		}
		// if item is available for shipping, return ship, else return bopus
		return canShip ? FulfillmentType.StandardShipping : FulfillmentType.InStorePickup;
	}

	// if cart has items for bopus
	if (hasPickupItems) {
		// if item is available for bopus, return bopus, else return ship
		return canPickup ? FulfillmentType.InStorePickup : FulfillmentType.StandardShipping;
	}

	// if cart has items for LD
	if (hasDeliveryItems) {
		// if item is available for LD, return LD
		if (canDeliver) {
			return FulfillmentType.LocalDelivery;
		}
		// if not available for LD: if is available for ship, return ship, else return bopus
		return canShip ? FulfillmentType.StandardShipping : FulfillmentType.InStorePickup;
	}

	// default: if unavailable for ship return bopus else return ship
	return !canShip ? FulfillmentType.InStorePickup : FulfillmentType.StandardShipping;
}

async function addToCartClick(event) {
	if (canAddToCart.value) {
		// this seemed to prevent JAWS focus jumping to the top of the page on click. Unclear why.
		event.preventDefault();
	}

	if (!canAddToCart.value) {
		onProductLinkClick(event);
	} else if (canAddToCart.value && !loading.value && !productAdded.value) {
		onProductLinkClick(event);

		const fulfillmentType = getExpectedFulfillmentMethod();

		loading.value = props.product.blainNumber;

		const response = await productService.addToCart({
			blainNumber: props.product.blainNumber,
			qty: 1,
			fulfillmentType: fulfillmentType,
			userConfirmed: true,
			userAgreed: false, // cannot add restricted products from product list page
			updateSubscriptionRequest: null,
			skipUpsell: true,
			ignoreFulfillmentType: true
		});

		handleAddToCartResponse(response, fulfillmentType);
	}
}

function handleAddToCartResponse(response: IPDPAddToCartResponse | null | undefined, fulfillmentType: FulfillmentType): void {
	// future: is there enough overlap between this and the ATC button from QuickAddProductDetails to warrant a shared component?

	if (response?.result === AddToCartResult.Success) {
		productAdded.value = true;
		productAddError.value = false;
		if (response.cartSummary) {
			window.vueEventBus?.$emit(EventBusEvents.UpdateQuickCart, response.cartSummary);
		}

		// Add to Cart event for Google Tag Manager
		const cartProduct = response.cartSummary.productsInCart.find(cartProduct => cartProduct.blainNumber === props.product.blainNumber);
		const orderProducts = response.cartSummary.productsInCart.map(item => ({
			blainNumber: item.price.blainNumber,
			qty: item.qty,
			unitPrice: item.price.price,
			vendorBrand: item.vendorBrand,
			productName: item.productName,
			imageUrl: item.imageUrlLarger,
			productUrl: item.virtualURL
		}));

		window.blainGtmDataLayer.push({ orderProducts: null }); // orderProducts needs to be reset first to avoid conflicts
		window.blainGtmDataLayer.push({
			'blainNumber': props.product.blainNumber,
			'vendorBrand': props.product.vendorBrand,
			'productName': props.product.productName,
			'deliveryChannel': fulfillmentType,
			'totalValue': cartProduct.unitPrice,
			'qty': 1,
			'orderProducts': orderProducts,
			'orderId': response.orderID,
			'event': "cart_ajax_add"
		});
	} else {
		productAddError.value = true;
	}

	loading.value = '';
}

onBeforeMount(() => {
	if (props.product.productSwatches.length <= 1) return;
	activeSwatch.value = props.product.productSwatches.find(ps => ps.largeImage == props.product.imageUrl);
});

return { __sfc: true,store, productService, props, emit, bopusOnly, loading, wrapperClasses, productImage, productImageWebp, flagText, hasFlag, activeProductImage, activeProductImageWebp, activeSwatchColor, productImageAltText, isProductInCart, canAddToCart, atcAriaLabel, activeSwatch, shouldImageTransition, productAdded, productAddError, onSwatchChange, onProductLinkClick, getExpectedFulfillmentMethod, addToCartClick, handleAddToCartResponse, BButton, LazyImage, Price, ProductFlag, ProductStockInfo, ProductSwatches, PromoLabel, ReviewStars }
}

})